import { Component, HostListener, inject, NgZone, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import { ThemeService } from '../../../common/services/theme.service';

/** Admin v2 (/admin-v2/**) brings its own chrome; the legacy navbar would stack above it. */
export function isAdminV2Url(url: string): boolean {
    const path = url.split(/[?#]/)[0];
    return path === '/admin-v2' || path.startsWith('/admin-v2/');
}

/** The v2 UI (/v2/**) brings its own chrome too. False for lookalikes such as '/v2x'. */
export function isV2Url(url: string): boolean {
    const path = url.split(/[?#]/)[0];
    return path === '/v2' || path.startsWith('/v2/');
}

/** The access pages (/access/**) bring their own full-page shell (mockup access-v1).
 *  Not /access/company: the classic, signed-in company profile, which keeps the navbar. */
export function isAccessUrl(url: string): boolean {
    const path = url.split(/[?#]/)[0];
    if (path === '/access/company' || path.startsWith('/access/company/')) return false;
    return path === '/access' || path.startsWith('/access/');
}

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnDestroy {
    title = 'JetFuelTenders.com';
    public showScrollTop = false;

    private scrollIdleTimer: ReturnType<typeof setTimeout> | null = null;
    private readonly scrollIdleDelay = 1500;
    private readonly scrollThreshold = 300;

    private readonly router = inject(Router);

    private readonly url = toSignal(
        this.router.events.pipe(
            filter((e): e is NavigationEnd => e instanceof NavigationEnd),
            map((e) => e.urlAfterRedirects),
        ),
        { initialValue: this.router.url },
    );

    /** True on /admin-v2 routes: the legacy navbar is not rendered there. */
    public readonly adminV2 = () => isAdminV2Url(this.url());

    /** True on /v2 routes: neither the legacy navbar nor the settings shell renders there. */
    public readonly v2 = () => isV2Url(this.url());

    /** True on /access routes: the legacy navbar would stack above the access shell. */
    public readonly access = () => isAccessUrl(this.url());

    constructor(private themeService: ThemeService, private ngZone: NgZone) {
        this.themeService.init();
    }

    @HostListener('window:dragover', ['$event'])
    @HostListener('window:drop', ['$event'])
    onDragDrop(event: Event) {
        event.preventDefault();
    }

    public onScroll(event: any) {
        const target = event.target;
        if (!target) return;

        const isAtBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 1;
        const isPastThreshold = target.scrollTop > this.scrollThreshold;

        this.showScrollTop = isPastThreshold && !isAtBottom;

        if (this.scrollIdleTimer) {
            clearTimeout(this.scrollIdleTimer);
        }

        if (this.showScrollTop) {
            this.ngZone.runOutsideAngular(() => {
                this.scrollIdleTimer = setTimeout(() => {
                    this.ngZone.run(() => {
                        this.showScrollTop = false;
                    });
                }, this.scrollIdleDelay);
            });
        }
    }

    ngOnDestroy() {
        if (this.scrollIdleTimer) {
            clearTimeout(this.scrollIdleTimer);
        }
    }

    public scrollToTop() {
        const mainElement = document.querySelector('.app-main');
        if (mainElement) {
            mainElement.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }
}
